import { normalizeContextualNoteHighlightMarkup } from "./contextualNoteHighlights";
import { decodeSafeMarkdownCharacterReferences } from "./htmlCharacterReferences";

/**
 * Normalize markdown patterns that are unstable across editor load/save
 * round-trips (heading spacing and HTML-like block indent).
 */
export const normalizeNoteMarkdown = (markdown: string): string => {
  if (!markdown) return markdown;

  return transformOutsideFencedCodeBlocks(markdown, (value) => {
    let normalized = normalizeContextualNoteHighlightMarkup(value);

    // NOV-1721: Plate encodes a trailing space (and some letters) as
    // `&#x20;` / `&#x54;`. Turn the safe ones back into characters before
    // the note is saved. Leave fenced and inline code alone.
    normalized = decodeSafeReferencesOutsideInlineCode(normalized);

    normalized = convertBrTagsToLineBreaks(normalized);

    normalized = separateParagraphsFromLists(normalized);

    normalized = normalized.replace(/([^\n])\n(#{1,6}\s)/g, "$1\n\n$2");

    // Avoid schema issues on indented HTML-like block lines (NOV-1204).
    normalized = normalized.replace(/(^|\n) {1,3}(<[!?/A-Za-z])/g, "$1$2");

    return normalized;
  });
};

// LLM output sometimes contains hard-break tags, including malformed variants
// with stray backslashes (`<br\ />`, `<br \/>`, `<br\/>`), which the editor
// shows as literal text (NOV-1679). Convert them to real markdown line breaks.
const BR_TAG_RE = /<\/?br[\s\\/]*>/i;
const BR_TAG_SPLIT_RE = /<\/?br[\s\\/]*>/gi;
// GFM table cells legitimately use <br> for in-cell line breaks; a real
// newline there would break the row.
const TABLE_ROW_RE = /^[ \t]*\|.*\|[ \t]*$/;
// GFM delimiter row with at least two columns (outer pipes optional, e.g.
// `--- | :---:`). Single-column tables require outer pipes, so their rows are
// already covered by TABLE_ROW_RE.
const TABLE_DELIMITER_ROW_RE =
  /^[ \t]*\|?(?:[ \t]*:?-+:?[ \t]*\|)+[ \t]*:?-+:?[ \t]*\|?[ \t]*$/;
const INLINE_CODE_RE = /(`+)[\s\S]*?\1/g;
const ENTITY_CODE_PLACEHOLDER_RE = /\uE000NOVONOTE_ENTITY_CODE_(\d+)\uE001/g;

function decodeSafeReferencesOutsideInlineCode(value: string): string {
  if (!value.includes("&")) return value;

  const codeSpans: string[] = [];
  const protectedValue = value.replace(INLINE_CODE_RE, (span) => {
    const index = codeSpans.push(span) - 1;
    return `\uE000NOVONOTE_ENTITY_CODE_${index}\uE001`;
  });
  const decoded = decodeSafeMarkdownCharacterReferences(protectedValue);

  return decoded.replace(
    ENTITY_CODE_PLACEHOLDER_RE,
    (match, index: string) => codeSpans[Number(index)] ?? match
  );
}
const BR_CODE_PLACEHOLDER_RE = /\uE000NOVONOTE_BR_CODE_(\d+)\uE001/g;

/**
 * Convert `<br>` tag variants in prose into markdown line breaks, leaving
 * inline code spans and GFM table blocks untouched. Fenced code is already
 * protected by the caller.
 */
function convertBrTagsToLineBreaks(value: string): string {
  if (!BR_TAG_RE.test(value)) return value;

  const codeSpans: string[] = [];
  const protectedValue = value.replace(INLINE_CODE_RE, (span) => {
    const index = codeSpans.push(span) - 1;
    return `\uE000NOVONOTE_BR_CODE_${index}\uE001`;
  });

  const lines = protectedValue.split("\n");
  const tableLines = findTableLineIndices(lines);
  const converted = lines
    .map((line, index) =>
      tableLines.has(index) ? line : convertBrTagsInLine(line)
    )
    .join("\n");

  return converted.replace(
    BR_CODE_PLACEHOLDER_RE,
    (match, index: string) => codeSpans[Number(index)] ?? match
  );
}

/**
 * Find lines that belong to a delimiter-defined GFM table block (header row,
 * delimiter row, and following pipe-containing data rows). Needed because
 * outer pipes are optional in GFM, so TABLE_ROW_RE alone misses rows like
 * `Anxiety<br/>Panic | Mild`. A data row must contain `|`; a blank line or
 * pipe-less prose (headings, lists, `<br>`) ends the table.
 */
function findTableLineIndices(lines: string[]): Set<number> {
  const tableLines = new Set<number>();

  for (let index = 1; index < lines.length; index++) {
    if (!TABLE_DELIMITER_ROW_RE.test(lines[index])) continue;

    const headerLine = lines[index - 1];
    if (!headerLine.trim() || !headerLine.includes("|")) continue;

    tableLines.add(index - 1);
    tableLines.add(index);
    for (let row = index + 1; row < lines.length; row++) {
      if (!lines[row].trim() || !lines[row].includes("|")) break;
      tableLines.add(row);
    }
  }

  return tableLines;
}

/**
 * Convert the `<br>` tag variants within a single prose line: tags at the
 * line edges become paragraph spacing, tags between text become markdown
 * hard breaks.
 */
function convertBrTagsInLine(line: string): string {
  if (!BR_TAG_RE.test(line) || TABLE_ROW_RE.test(line)) return line;

  const segments = line.split(BR_TAG_SPLIT_RE);

  // Tags at the end of a line: the following newline already breaks the line.
  while (segments.length > 1 && !segments[segments.length - 1].trim()) {
    segments.pop();
  }

  // Tags at the start of a line become a blank line (paragraph spacing).
  let prefix = "";
  while (segments.length > 1 && !segments[0].trim()) {
    segments.shift();
    prefix = "\n";
  }

  // The line contained nothing but break tags.
  if (segments.every((segment) => !segment.trim())) {
    return "";
  }

  // A hard break inside an ATX heading is not valid markdown and leaves a
  // visible backslash (`# Title<br/>Sub` → `# Title\`). Fold the segments
  // onto the heading line instead.
  if (/^[ \t]*#{1,6}[ \t]/.test(line)) {
    return (
      prefix +
      segments
        .map((segment) => segment.trim())
        .filter(Boolean)
        .join(" ")
    );
  }

  // Tags between text become markdown hard breaks.
  return prefix + segments.join("\\\n");
}

const LIST_ITEM_RE = /^ {0,3}(?:[-+*]|\d{1,9}[.)])[ \t]+\S/;
const HARD_BREAK_END_RE = /(?:\\| {2,})\r?$/;

/**
 * CommonMark folds an unindented line right after a list item into that item
 * (a lazy continuation), so LLM output like `- Sleep poor\n**Plan**: ...`
 * puts the next section inside the last bullet. Start a new paragraph
 * instead. A line after a hard break, or one that continues in lowercase
 * (a soft-wrapped sentence), is still part of the item.
 */
function separateParagraphsFromLists(value: string): string {
  const lines = value.split("\n");
  const result: string[] = [];
  let inListItem = false;

  for (const line of lines) {
    const previous = result[result.length - 1] ?? "";
    if (LIST_ITEM_RE.test(line)) {
      inListItem = true;
    } else if (!line.trim()) {
      inListItem = false;
    } else if (
      inListItem &&
      !/^[ \t]/.test(line) &&
      !/^\p{Ll}/u.test(line) &&
      !HARD_BREAK_END_RE.test(previous)
    ) {
      result.push("");
      inListItem = false;
    }
    result.push(line);
  }

  return result.join("\n");
}

export function transformOutsideFencedCodeBlocks(
  markdown: string,
  transform: (value: string) => string
): string {
  const parts = markdown.split(/(\r?\n)/);
  let fence: { character: string; length: number } | null = null;
  let pending = "";
  let result = "";

  for (let index = 0; index < parts.length; index += 2) {
    const line = parts[index] ?? "";
    const newline = parts[index + 1] ?? "";
    const fenceMatch = line.match(/^[ \t]{0,3}(`{3,}|~{3,})/);

    if (!fence && fenceMatch) {
      result += transform(pending);
      pending = "";
      fence = {
        character: fenceMatch[1][0],
        length: fenceMatch[1].length,
      };
      result += line + newline;
      continue;
    }

    if (fence) {
      result += line + newline;
      if (
        fenceMatch &&
        fenceMatch[1][0] === fence.character &&
        fenceMatch[1].length >= fence.length &&
        /^[ \t]*$/.test(line.slice(fenceMatch[0].length))
      ) {
        fence = null;
      }
      continue;
    }

    pending += line + newline;
  }

  return result + transform(pending);
}
